import { memo, useCallback, useMemo, type ElementType, type FunctionComponent, type ReactNode } from 'react';
import size from 'lodash/size';
import isNil from 'lodash/isNil';
import { Link as RouterLink } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
// Material Icon imports
import type { InputBaseComponentProps, SxProps, Theme } from '@mui/material';
import EditSquareIcon from '@mui/icons-material/EditSquare';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
// Skillmore UI Components
import { type ExportFormat } from '@empathco/ui-components/src/models/exportFormat';
import { bold } from '@empathco/ui-components/src/helpers/intl';
import useConfirmationDialog from '@empathco/ui-components/src/hooks/useConfirmationDialog';
import ExportButton, { type ExportParams } from '@empathco/ui-components/src/elements/ExportButton';
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
import ConfirmDialog from '@empathco/ui-components/src/elements/ConfirmDialog';
// local imports
import useEditTitle from '../hooks/useEditTitle';
import useExport from '../hooks/useExport';
import ScrollableTitleInput from './ScrollableTitleInput';
// SCSS imports
import { outlinedRoot, outlined, root, input } from './ReviewBar.module.scss';

const classes = { root: outlinedRoot, input };
const inputLabelProps = { classes: { root, outlined } };

// Read-only title renders through the scrollable div renderer (non-focusable) — so no caret/focus
// rules are reachable; only the outline (on the still-rendered OutlinedInput root) needs suppressing,
// including the hover state.
const readOnlyInputSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-notchedOutline': { border: 0 },
  '&:hover .MuiOutlinedInput-notchedOutline': { border: 0 }
} as const;

const scrollableInputComponent = ScrollableTitleInput as unknown as ElementType<InputBaseComponentProps>;

// Save/Cancel actions shown inside the title field while editing.
type EditAdornmentProps = {
  readonly disabled?: boolean;
  readonly canSave: boolean;
  readonly onSave: () => void;
  readonly onCancel: () => void;
};

const EditAdornment: FunctionComponent<EditAdornmentProps> = ({
  disabled = false,
  canSave,
  onSave,
  onCancel
}) => {
  const { formatMessage } = useIntl();
  const saveLabel = formatMessage({ id: 'common.button.save' });
  const cancelLabel = formatMessage({ id: 'common.button.cancel' });
  return (
    <InputAdornment position="end">
      <IconButton
          aria-label={saveLabel}
          disabled={disabled || !canSave}
          onClick={onSave}
      >
        <CheckIcon/>
      </IconButton>
      <IconButton
          aria-label={cancelLabel}
          disabled={disabled}
          onClick={onCancel}
      >
        <CloseIcon/>
      </IconButton>
    </InputAdornment>
  );
};

type ReviewBarProps = {
  readonly titleLabel: string;
  readonly title?: string;
  readonly link?: string;
  readonly avatar?: ReactNode;
  readonly status?: ReactNode | null;
  readonly action?: ReactNode;
  readonly pending?: boolean;
  readonly disabled?: boolean;
  readonly editLabel: string;
  readonly onRename: (title?: string) => void;
  readonly renameLabel: string;
  readonly renamePending?: boolean | null;
  readonly renameFailed?: boolean | null;
  readonly renameError: string;
  readonly onDelete: () => unknown;
  readonly deleteLabel: string;
  readonly deleteTitle?: string;
  readonly deleteQuestion: string;
  readonly deletePending?: boolean | null;
  readonly deleteFailed?: boolean | null;
  readonly deleteError: string;
  readonly exportParams?: ExportParams | null;
  readonly exportEndpoint?: string;
  readonly onExport?: (format: ExportFormat, title: string) => Promise<number | boolean> | null;
  readonly exportDisabled?: boolean;
  // for Storybook only
  readonly testCase?: number;
}

const ReviewBar: FunctionComponent<ReviewBarProps> = ({
  titleLabel,
  title: parentTitle,
  link,
  avatar,
  status,
  action,
  pending = false,
  disabled: disabledParent = false,
  editLabel,
  onRename,
  renameLabel,
  renamePending = false,
  renameFailed = false,
  renameError,
  onDelete,
  deleteLabel,
  deleteTitle,
  deleteQuestion,
  deletePending = false,
  deleteFailed = false,
  deleteError,
  exportParams,
  exportEndpoint,
  onExport,
  exportDisabled = false,
  testCase
  // eslint-disable-next-line complexity
}) => {
  const { formatMessage } = useIntl();

  const titleText = useMemo(() => formatMessage({ id: titleLabel }), [titleLabel, formatMessage]);

  const {
    inputRef,
    title,
    editing,
    canSave,
    handleRename,
    handleTitleChange,
    handleSave,
    handleCancel: handleEditCancel
  } = useEditTitle({
    parentTitle,
    renamePending,
    renameFailed,
    updateTitle: onRename
  });

  const disabled = disabledParent || renamePending || deletePending ? true : undefined;

  const effectiveEditing = editing || testCase === 1 || testCase === 2;
  const effectiveCanSave = canSave || testCase === 2;

  const slotProps = useMemo<TextFieldProps['slotProps']>(() => {
    const startAdornment = pending ? (
      <InputAdornment position="start">
        <Box color="action.disabled" component="span">
          <CircularProgress size={28} color="inherit"/>
        </Box>
      </InputAdornment>
    ) : undefined;
    let endAdornment: ReactNode;
    if (!pending && renamePending) {
      endAdornment = (
        <InputAdornment position="end">
          <Box color="text.disabled" component="span">
            <CircularProgress size={28} color="inherit"/>
          </Box>
        </InputAdornment>
      );
    } else if (!pending && effectiveEditing) {
      endAdornment = (
        <EditAdornment
            disabled={disabled}
            canSave={effectiveCanSave}
            onSave={handleSave}
            onCancel={handleEditCancel}
        />
      );
    }
    // Storybook only: ReviewBar testCase 3/4 forces the scroll indicators (→ element testCase 1/2).
    let scrollTestCase: number | undefined;
    if (testCase === 3) scrollTestCase = 1;
    else if (testCase === 4) scrollTestCase = 2;
    const htmlInput: InputBaseComponentProps & { testCase?: number } = effectiveEditing
      ? { readOnly: false, tabIndex: 0 }
      : { readOnly: true, tabIndex: -1, testCase: scrollTestCase };
    return {
      input: {
        classes,
        ...effectiveEditing ? {} : { sx: readOnlyInputSx, inputComponent: scrollableInputComponent },
        ...startAdornment ? { startAdornment } : {},
        ...endAdornment ? { endAdornment } : {}
      },
      inputLabel: inputLabelProps,
      htmlInput
    };
  }, [pending, renamePending, effectiveEditing, effectiveCanSave, disabled, testCase, handleSave, handleEditCancel]);

  const dialogValues = useMemo(() => ({ title, bold }), [title]);

  const {
    confirmOpen,
    confirmMounted,
    confirmPending,
    handleAction: handleDelete,
    handleCancel,
    handleExited,
    handleConfirm
  } = useConfirmationDialog(onDelete, { holdWhilePending: true });

  const getExport = useCallback((format: ExportFormat, _token: string) => {
    if (exportDisabled || !title || !onExport) return null;
    return onExport(format, title);
  }, [exportDisabled, title, onExport]);

  const exp = useExport(getExport);

  return (
    <>
      <Box
          flexGrow={1}
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="flex-start"
          columnGap={1.5}
          rowGap={1}
          px={2}
          pt={1}
          pb={status ? undefined : 1}
      >
        {avatar}
        <Box
            display="flex"
            flexGrow={1}
            flexBasis={0}
            minWidth={{ xs: avatar ? 'calc(100% - 4.5rem)' : '100%', sm: '18rem' }}
            pl={avatar ? 0.25 : undefined}
            alignItems="center"
            justifyContent="flex-start"
        >
          <TextField
              inputRef={inputRef}
              variant="outlined"
              label={pending || size(title) >= 1 ? undefined : titleText}
              margin="dense"
              size="small"
              fullWidth
              value={isNil(title) ? '' : title}
              onChange={handleTitleChange}
              disabled={disabled}
              slotProps={slotProps}
          />
        </Box>
        {action ? (
          <Box display="flex" justifyContent="flex-end">
            {action}
          </Box>
        ) : undefined}
        <Box display="flex" justifyContent="flex-end">
          <Button
              variant="text"
              color="primary"
              startIcon={<DriveFileRenameOutlineRoundedIcon/>}
              onClick={handleRename}
              disabled={disabled || effectiveEditing}
          >
            <FormattedMessage id={renameLabel}/>
          </Button>
        </Box>
        {link ? (
          <Box display="flex" justifyContent="flex-end">
            <Button
                variant="text"
                color="primary"
                startIcon={<EditSquareIcon/>}
                component={RouterLink}
                to={link}
                disabled={disabled || size(link) < 1}
            >
              <FormattedMessage id={editLabel}/>
            </Button>
          </Box>
        ) : undefined}
        <Box display="flex" justifyContent="flex-end">
          <Button
              variant="text"
              color="primary"
              startIcon={deletePending ? <CircularProgress size={18} color="inherit"/> : <DeleteIcon/>}
              onClick={handleDelete}
              disabled={disabled}
          >
            <FormattedMessage id={deleteLabel}/>
          </Button>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <ExportButton
              pending={onExport ? exp.failed === false : undefined}
              disabled={disabled || !title || exportDisabled || (
                onExport && (exp.failed === false || !exp.enabled)
              )}
              params={onExport ? undefined : exportParams}
              endpoint={onExport ? undefined : exportEndpoint}
              onExport={onExport ? exp.handleExport : undefined}
          />
        </Box>
      </Box>
      {status}
      {title && confirmMounted ? (
        <ConfirmDialog
            open={confirmOpen}
            title={deleteTitle || deleteQuestion}
            text={deleteTitle ? deleteQuestion : undefined}
            withExtraPadding={!deleteTitle}
            withCancelButton
            values={dialogValues}
            pending={confirmPending}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            onExited={handleExited}
        />
      ) : undefined}
      <ActionSnackbar
          severity="error"
          message={renameError}
          open={renameFailed}
      />
      <ActionSnackbar
          severity="error"
          message={deleteError}
          open={deleteFailed}
      />
    </>
  );
};

export default memo(ReviewBar);
